import { Divider, Grid, Card as MuiCard, CardContent as MuiCardContent, Typography as MuiTypography, Skeleton } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, spacing } from '@mui/system';
import { Fragment, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

const Card = styled(MuiCard)<{ background?: string; display?: string }>`
  position: relative;
  border: 1px solid ${() => grey[200]};
  height: 100%;
  box-sizing: border-box;
  ${(props) =>
    props.background &&
    css`
      background: ${props.theme.palette.background[props.background]};
    `}
  ${(props) =>
    props.display &&
    css`
      display: ${props.display};
      height: auto;
    `}
  /* min-width keeps a tile readable when the grid lets it size itself, but it
     must never exceed the cell it was given: at 200px in a 175px column the
     cards overlapped their neighbours. */
  max-width: 100%;
  ${(props) => props.theme.breakpoints.up('lg')} {
    min-width: min(200px, 100%);
  }
  ${(props) => props.theme.breakpoints.up('xl')} {
    min-width: min(220px, 100%);
  }
`;

const Typography = styled(MuiTypography)(spacing);

const VerticalDivider = styled(Divider)`
  border: 1px solid ${(props) => props.theme.palette.text.primary};
`;

const CardContent = styled(MuiCardContent)`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  height: inherit;
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(1)};
  }
`;

const Amount = styled.span<{
  color: string;
}>`
  color: ${(props) => props.theme.palette.text[props.color]};
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  /* One metric size everywhere, a quarter smaller than the original full size.
     Dense tiles used to run their own 24px; they now match so a KPI reads the
     same on every page that shows one. */
  font-size: 18px;
  line-height: 1;
  ${(props) => props.theme.breakpoints.up('sm')} {
    font-size: 21px;
  }
  ${(props) => props.theme.breakpoints.up('lg')} {
    font-size: 30px;
  }
`;

const SkeletonStyles = styled(Skeleton)`
  width: 20px;
  height: 100%;
`;

// Labels must not wrap: a wrapped label makes its own column taller than the
// rest of the row, which is what pulled the labels out of line with the amounts
// in the first place. Tiles carrying four metrics drop a size so the row still
// fits the card. On a phone the size is $phone, a CSS length fitted to the tile.
const labelStyles = css<{ $compact?: boolean; $phone?: string }>`
  color: ${(props) => props.theme.palette.text.primary};
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  white-space: nowrap;
  ${(props) =>
    props.$compact &&
    css`
      font-size: 12px;
    `}
  ${(props) =>
    props.$phone &&
    css`
      font-size: ${props.$phone};
    `}
`;

const LinkLabel = styled(Link)<{ $compact?: boolean; $phone?: string }>`
  ${labelStyles}
  color: ${(props) => props.theme.palette.text.primary} !important;
`;
const LabelBox = styled(Box)<{ $compact?: boolean; $phone?: string }>`
  ${labelStyles}
`;

const LinkAmount = styled(Link)`
  &:hover {
    text-decoration: none;
  }
`;

type DataType = {
  // Strings are for values that carry a unit, like a formatted percentage; they
  // render in the same large style as a plain count.
  amount?: number | string;
  label?: string;
  color: string;
  link?: string;
  // An optional third line under the label, for context the number needs to be
  // read properly -- what a normal period looks like, say. Kept small and
  // secondary so it never competes with the metric itself.
  caption?: ReactNode;
};

type KPIProps = {
  title?: ReactNode;
  data: DataType[];
  fetching?: boolean;
  background?: 'pink' | 'grey';
  display?: string;
  dense?: boolean;
};

// data-testid slug for e2e tests: "Equip Location" -> "equip-location", "On-JobSite" -> "on-jobsite".
// A tile is kpi-<title>; each amount is kpi-value-<label>, or kpi-value when it has no label.
const testSlug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// `dense` no longer changes the type sizes; those are the same on every page.
// It only tightens the gap between metrics inside a multi-metric tile.
export default function KPI({ title, data, fetching, background, display }: KPIProps): ReactElement {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const gotLabel = data.some((item) => item.label);
  const gotCaption = data.some((item) => item.caption);
  // Four metrics only fit the card at a smaller label size and a tighter gap.
  const compact = data.length > 3;
  // Phones fit two tiles to a row and keep every metric on one line. Labels are
  // at most 10px and shrink to whatever the tile's width allows: the metric row
  // is a size container, and the label size is its width, less the dividers,
  // over the labels' total width per px of font (Inter semibold runs about
  // 0.56em a character). Amounts are narrower than their labels, so the labels
  // decide the fit.
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const phone = !isSmUp;
  const phoneGap = 2;
  const dividers = (data.length - 1) * (2 + 2 * phoneGap);
  const labelEms = 0.56 * data.reduce((sum, item) => sum + (item.label?.length ?? 0), 0);
  // The same fitting on every screen, not just phones. A tile sits in a grid
  // cell that can be squeezed below the width its labels want, and the card
  // clips what does not fit -- which is how a row of tiles ends up with its
  // first and last characters shaved off. Sizing the labels to the width the
  // tile actually got means they shrink instead of being cut.
  const fullLabel = compact ? 12 : 14;
  const fittedLabel = labelEms ? `min(${phone ? 10 : fullLabel}px, calc((100cqi - ${dividers}px) / ${labelEms.toFixed(2)}))` : undefined;

  return (
    <Card background={background} display={display} data-testid={typeof title === 'string' ? `kpi-${testSlug(title)}` : undefined}>
      <CardContent sx={{ p: phone && data.length > 2 ? 0.5 : 1 }}>
        <Grid container flexDirection="column" sx={{ height: 'inherit', width: '100%' }}>
          {title ? (
            <Grid item>
              <Typography sx={{ fontSize: isMdUp ? 22 : 18, textAlign: 'center' }} variant="h3">
                {title}
              </Typography>
            </Grid>
          ) : null}
          <Grid item sx={{ containerType: 'inline-size', minWidth: 0, width: '100%' }}>
            {/* One grid column per metric, two rows: the amount and its label
                share a column, so a wide label like "Scheduled" cannot push its
                number out from under it. Dividers get their own column, tall
                piece on the amount row and short piece on the label row. */}
            <Box
              sx={{
                display: 'grid',
                gridAutoFlow: 'column',
                // Columns share whatever width the tile got instead of
                // demanding their content width and being clipped by the card.
                gridAutoColumns: 'minmax(0, auto)',
                maxWidth: '100%',
                gridTemplateRows: `auto${gotLabel ? ' auto' : ''}${gotCaption ? ' auto' : ''}`,
                justifyContent: 'center',
                justifyItems: 'center',
                alignItems: 'center',
                columnGap: phone ? `${phoneGap}px` : compact ? 1 : 2,
              }}
            >
              {data.map((item, index) => {
                const AmountToShow = (
                  <Amount color={item.color} data-testid={item.label ? `kpi-value-${testSlug(item.label)}` : 'kpi-value'}>
                    {fetching ? <SkeletonStyles animation="wave" /> : item.amount === undefined ? <span>&nbsp;</span> : item.amount}
                  </Amount>
                );
                return (
                  <Fragment key={index.toString()}>
                    {index !== 0 && (
                      <Fragment>
                        <VerticalDivider orientation="vertical" flexItem sx={{ height: 20 }} />
                        {/* The label row gets an empty cell, not a second rule:
                          the divider above already separates the pair, and a
                          second one crowds the labels. The cell still has to
                          be emitted to keep the column flow aligned. */}
                        {gotLabel && <span />}
                        {gotCaption && <span />}
                      </Fragment>
                    )}
                    {item.link ? <LinkAmount to={item.link}>{AmountToShow}</LinkAmount> : AmountToShow}
                    {gotLabel &&
                      (item.link ? (
                        <LinkLabel to={item.link} $compact={compact} $phone={fittedLabel}>
                          {item.label}
                        </LinkLabel>
                      ) : (
                        <LabelBox $compact={compact} $phone={fittedLabel}>
                          {item.label}
                        </LabelBox>
                      ))}
                    {gotCaption && (
                      <Box
                        sx={{
                          fontSize: phone ? 9 : 11,
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                          mt: 0.25,
                        }}
                      >
                        {fetching ? '' : item.caption}
                      </Box>
                    )}
                  </Fragment>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
